/* Shared safety UI — the report WIZARD and a generic in-app confirm.
   Used by ProfileDetail (discover) and the chat thread overflow.

   Why these exist: Telegram WebViews block synchronous dialogs (window.confirm /
   window.alert) the same way they block navigator.geolocation — so the old
   confirm-then-act flows silently did NOTHING inside Telegram. These render
   inside the app (a Sheet), so Block/Report actually fire. */
import React from 'react';
import { Sheet, Icon, Button } from './ui';

/* i18n key → API ReportReason (POST /v1/reports). One source for both screens. */
export const REPORT_REASONS = [
  ['rrSpam', 'spam'],
  ['rrHarassment', 'harassment'],
  ['rrNudity', 'nudity'],
  ['rrScam', 'scam'],
  ['rrFake', 'fake_or_catfish'],
  ['rrOther', 'other'],
];

/* tiny step pill row: Reason → Details */
function Steps({ t, active }) {
  const items = [
    ['reason', t('reportStepReason')],
    ['details', t('reportStepDetails')],
  ];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
      {items.map(([key, label], i) => {
        const on = active === key || (active === 'sending' && key === 'details');
        return (
          <React.Fragment key={key}>
            <span style={{
              fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.06em',
              textTransform: 'uppercase', padding: '4px 9px', borderRadius: 99,
              color: on ? 'var(--accent)' : 'var(--text-3)',
              background: on ? 'rgba(255,212,145,0.12)' : 'var(--surface-2)',
              border: '1px solid ' + (on ? 'rgba(255,212,145,0.4)' : 'var(--border)'),
            }}>{label}</span>
            {i === 0 ? <span style={{ flex: 'none', width: 14, height: 1, background: 'var(--border-bright)' }} /> : null}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/**
 * Report wizard (bottom sheet), three steps:
 *   1. reason  — pick one of REPORT_REASONS
 *   2. details — optional free-text comment, then "Send report"
 *   3. sent    — confirmation, auto-closes
 * `onSubmit(reason, detail)` MUST return a Promise. The server blocks the target
 * in the same transaction, so the success copy says so.
 */
export function ReportSheet({ open, name, t, onSubmit, onClose, onDone }) {
  const [step, setStep] = React.useState('reason'); // reason | details | sending | sent | error
  const [reason, setReason] = React.useState(null);
  const [detail, setDetail] = React.useState('');

  React.useEffect(() => {
    if (open) { setStep('reason'); setReason(null); setDetail(''); }
  }, [open]);

  const send = () => {
    setStep('sending');
    Promise.resolve(onSubmit(reason, detail.trim() || undefined))
      .then(() => {
        setStep('sent');
        setTimeout(() => (onDone ? onDone() : onClose()), 1500);
      })
      .catch(() => setStep('error'));
  };

  const busy = step === 'sending';
  return (
    <Sheet open={open} onClose={busy ? () => {} : onClose}>
      {step === 'sent' ? (
        <div data-screen-label="Report sent" style={{ textAlign: 'center', padding: '8px 4px 4px' }}>
          <span style={{
            width: 58, height: 58, borderRadius: '50%', margin: '0 auto 14px', display: 'flex',
            alignItems: 'center', justifyContent: 'center', background: 'rgba(255,212,145,0.14)',
            border: '1px solid rgba(255,212,145,0.4)',
          }}>
            <Icon name="check" size={26} color="var(--accent)" strokeWidth={2.5} />
          </span>
          <h3 className="flm-display" style={{ fontSize: 22, color: 'var(--text)', margin: '0 0 8px' }}>{t('reportSentTitle')}</h3>
          <p style={{ margin: 0, fontFamily: 'var(--font-body)', fontSize: 13.5, lineHeight: 1.55, color: 'var(--text-2)' }}>{t('reportSentSub')}</p>
        </div>
      ) : (
        <div data-screen-label="Report wizard">
          <span className="flm-eyebrow">
            <span className="flm-dot is-love"></span>
            <span className="flm-mono" style={{ color: 'var(--text-2)' }}>{t('report')}{name ? ' · ' + name : ''}</span>
          </span>
          <Steps t={t} active={step} />

          {step === 'reason' ? (
            <React.Fragment>
              <h3 className="flm-display" style={{ fontSize: 23, color: 'var(--text)', margin: '0 0 14px' }}>{t('reportWhy')}</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {REPORT_REASONS.map(([key, r]) => (
                  <button
                    key={r}
                    className="flm-chip"
                    onClick={() => { setReason(r); setStep('details'); }}
                    style={{ cursor: 'pointer', fontSize: 13 }}
                  >
                    {t(key)}
                  </button>
                ))}
              </div>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <h3 className="flm-display" style={{ fontSize: 23, color: 'var(--text)', margin: '0 0 6px' }}>{t('reportDetailsTitle')}</h3>
              <p style={{ margin: '0 0 12px', fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-3)' }}>{t('reportDetailsSub')}</p>
              <textarea
                value={detail}
                onChange={(e) => setDetail(e.target.value)}
                maxLength={1000}
                rows={4}
                placeholder={t('reportDetailsPlaceholder')}
                disabled={busy}
                style={{
                  width: '100%', boxSizing: 'border-box', resize: 'none', padding: '12px 14px',
                  borderRadius: 14, background: 'var(--surface-2)', border: '1px solid var(--border)',
                  color: 'var(--text)', fontFamily: 'var(--font-body)', fontSize: 14.5, lineHeight: 1.5,
                  outline: 'none',
                }}
              />
              {step === 'error' ? (
                <p style={{ margin: '8px 0 0', fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--love)' }}>{t('reportFailed')}</p>
              ) : null}
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <Button variant="outline" onClick={() => setStep('reason')} disabled={busy} style={{ flex: '0 0 auto', paddingInline: 22 }}>
                  {t('back')}
                </Button>
                <Button variant="primary" onClick={send} disabled={busy} style={{ flex: 1 }}>
                  {busy ? t('reportSending') : t('reportSend')}
                </Button>
              </div>
            </React.Fragment>
          )}
        </div>
      )}
    </Sheet>
  );
}

/**
 * Generic in-app confirm (replaces window.confirm). `onConfirm` may return a
 * Promise; while it's pending the buttons disable. The confirm action is styled
 * as the primary CTA; cancel is the outline button.
 */
export function ConfirmSheet({ open, t, title, body, confirmLabel, onConfirm, onClose }) {
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { if (open) setBusy(false); }, [open]);
  const go = () => {
    const r = onConfirm();
    if (r && typeof r.then === 'function') { setBusy(true); r.finally(() => setBusy(false)); }
  };
  return (
    <Sheet open={open} onClose={busy ? () => {} : onClose}>
      <div data-screen-label="Confirm">
        <h3 className="flm-display" style={{ fontSize: 23, color: 'var(--text)', margin: '2px 0 8px' }}>{title}</h3>
        {body ? <p style={{ margin: 0, fontFamily: 'var(--font-body)', fontSize: 14, lineHeight: 1.55, color: 'var(--text-2)' }}>{body}</p> : null}
        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <Button variant="outline" onClick={onClose} disabled={busy} style={{ flex: 1 }}>{t('cancel')}</Button>
          <Button variant="primary" onClick={go} disabled={busy} style={{ flex: 1 }}>{confirmLabel}</Button>
        </div>
      </div>
    </Sheet>
  );
}
